// Transform-only animation for animal overlay sprites
// Applies gentle bob, sway, and breathe transforms

const MAX_ANIM_ANIMALS = 80;

function fastHash(q, r, key) {
  let h = q * 374761393 + r * 668265263;
  for (let i = 0; i < key.length; i++) {
    h = ((h ^ key.charCodeAt(i)) * 2654435761) | 0;
  }
  return (h >>> 0) / 0xffffffff;
}

export function isAnimal(overlayKey) {
  return overlayKey != null && overlayKey.startsWith('overlays/animal/');
}

export class AnimalAnimator {
  constructor() {
    this._sortBuf = [];
  }

  /**
   * Draw an animal overlay with gentle animation.
   * @param {CanvasRenderingContext2D} ctx
   * @param {import('../assets/SpriteAtlas.mjs').SpriteAtlas} atlas
   * @param {string} overlayKey
   * @param {number} sx - screen x center
   * @param {number} sy - screen y center
   * @param {number} sz - screen hex size
   * @param {number} q - hex q coordinate (for deterministic seed)
   * @param {number} r - hex r coordinate
   * @param {number} nowMs
   * @param {number} spriteOffsetY - SPRITE_OFFSET_Y constant
   */
  draw(ctx, atlas, overlayKey, sx, sy, sz, q, r, nowMs, spriteOffsetY) {
    if (!atlas || !atlas.has(overlayKey)) return;

    const seed = fastHash(q, r, overlayKey);
    const timeSec = nowMs / 1000;

    // Deterministic phase offsets
    const bobPhase = seed * Math.PI * 2;
    const swayPhase = (seed * 3.7 + 0.5) * Math.PI * 2;
    const breathePhase = (seed * 5.3 + 0.2) * Math.PI * 2;

    // Frequencies (slightly varied per animal)
    const bobFreq = 0.8 + seed * 0.4;       // 0.8-1.2 Hz
    const swayFreq = 0.5 + seed * 0.3;      // 0.5-0.8 Hz
    const breatheFreq = 1.0 + seed * 0.5;   // 1.0-1.5 Hz

    // Amplitudes (subtle: 1-3px bob, ±1° sway, ±1.5% breathe)
    const bobAmp = 1.0 + seed * 2.0;
    const swayDeg = 0.5 + seed * 0.5;
    const breatheAmp = 0.005 + seed * 0.01;

    const bobY = Math.sin(timeSec * bobFreq * Math.PI * 2 + bobPhase) * bobAmp;
    const swayAngle = Math.sin(timeSec * swayFreq * Math.PI * 2 + swayPhase) * swayDeg * (Math.PI / 180);
    const breatheScale = 1 + Math.sin(timeSec * breatheFreq * Math.PI * 2 + breathePhase) * breatheAmp;

    const scale = (2 * sz) / 30;
    const dx = sx - 15 * scale;
    const dy = sy - 15 * scale + bobY;

    ctx.save();
    // Translate to sprite center, apply sway rotation and breathe scale, then draw
    ctx.translate(sx, sy);
    ctx.rotate(swayAngle);
    ctx.scale(breatheScale, breatheScale);
    ctx.translate(-sx, -sy);

    ctx.translate(dx, dy);
    ctx.scale(scale, scale);
    atlas.draw(ctx, overlayKey, 0, spriteOffsetY);
    ctx.restore();
  }

  /**
   * Filter and prioritize animals by distance to camera center.
   * Returns indices into visibleTiles that should be animated.
   * @param {Array} visibleTiles - array of { tile, sp }
   * @param {number} canvasW
   * @param {number} canvasH
   * @returns {Set<number>} indices to animate
   */
  prioritize(visibleTiles, canvasW, canvasH) {
    const cx = canvasW / 2;
    const cy = canvasH / 2;
    this._sortBuf.length = 0;

    for (let i = 0; i < visibleTiles.length; i++) {
      const { tile, sp } = visibleTiles[i];
      if (!isAnimal(tile.overlaySpriteKey)) continue;
      const dx = sp.x - cx;
      const dy = sp.y - cy;
      this._sortBuf.push({ idx: i, dist: dx * dx + dy * dy });
    }

    if (this._sortBuf.length > MAX_ANIM_ANIMALS) {
      this._sortBuf.sort((a, b) => a.dist - b.dist);
      this._sortBuf.length = MAX_ANIM_ANIMALS;
    }

    const result = new Set();
    for (const entry of this._sortBuf) {
      result.add(entry.idx);
    }
    return result;
  }
}
